<i18n>
{
  "cl": {
    "deliveryDate": "Fecha de entrega"
  }
}
</i18n>

<template>
  <b-collapse class="evaluation-card" animation="slide" data-cy="property-data" :open="isOpen">
    <template #trigger="{ open }">
      <div class="card-header" role="button" :aria-expanded="open.toString()">
        <p class="card-header-title">
          Datos del inmueble
        </p>
        <a class="card-header-icon">
          <b-icon :icon="open ? 'minus' : 'plus'" />
        </a>
      </div>
    </template>
    <div class="card-block p-0">
      <div class="subcard">
        <b-field label="Tipo de propiedad" horizontal>
          <b-select
            v-model="localSimulation.offerInputs.propertyType"
            :disabled="readOnly"
            size="is-small"
            placeholder="Selecciona un tipo de propiedad"
            data-cy="property-type"
            expanded>
            <option value="HOME">HOME</option>
            <option value="APARTMENT">APARTMENT</option>
          </b-select>
        </b-field>

        <b-field label="Estado" horizontal>
          <b-select
            v-model="localSimulation.offerInputs.isPropertyNew"
            :disabled="readOnly"
            size="is-small"
            b-model="localSimulation.property.isPropertyNew"
            placeholder="Selecciona un tipo de propiedad"
            data-cy="is-property-new"
            expanded>
            <option value="true">Nuevo</option>
            <option value="false">Usado</option>
          </b-select>
        </b-field>
      </div>
      <div class="subcard is-gray">
        <div class="subcard-header header-switch">
          <b-field label="¿Tiene dirección?" horizontal>
            <b-switch
              v-model="localSimulation.generalParams.hasAddress"
              :disabled="readOnly"
              size="is-small"
              data-cy="hasAddress" />
          </b-field>
        </div>
        <div v-if="localSimulation.generalParams.hasAddress" id="add-address" class="fields-has-text-right">
          <b-field label="Calle y número" horizontal>
            <b-input
              v-model="mainLineAddress"
              :disabled="readOnly"
              size="is-small"
              type="text"
              autocomplete="off"
              data-cy="main-line-address"
              expanded />
          </b-field>
          <b-field label="Departamento" horizontal>
            <b-input
              v-model="secondaryLineAddress"
              :disabled="readOnly"
              size="is-small"
              type="text"
              autocomplete="off"
              data-cy="secondary-line-address"
              expanded />
          </b-field>
          <b-field label="Comuna/Distrito" horizontal>
            <b-input
              v-model="localSimulation.offerInputs.district"
              :disabled="readOnly"
              size="is-small"
              type="text"
              autocomplete="off"
              expanded />
          </b-field>
          <b-field label="Ciudad" horizontal>
            <b-input
              v-model="cityAddress"
              :disabled="readOnly"
              size="is-small"
              autocomplete="off"
              type="text"
              expanded />
          </b-field>
          <b-field label="País" horizontal>
            <b-input
              v-model="countryAddress"
              :disabled="readOnly"
              size="is-small"
              type="text"
              autocomplete="off"
              expanded />
          </b-field>
        </div>
      </div>
      <div class="subcard is-gray">
        <div class="subcard-header header-switch">
          <b-field label="¿Es inmobiliaria?" horizontal>
            <b-switch
              v-model="localSimulation.generalParams.hasRealEstate"
              :disabled="readOnly"
              data-cy="hasRealEstate"
              size="is-small" />
          </b-field>
        </div>
        <div v-if="localSimulation.generalParams.hasRealEstate" id="add-real-state" class="fields-has-text-right">
          <b-field label="Nombre inmobiliaria" horizontal>
            <b-autocomplete
              v-model="realEstate"
              :disabled="readOnly"
              :data="filteredRealEstates()"
              :clearable="!readOnly"
              size="is-small"
              :keep-first="true"
              data-cy="real-estate-name"
              @typing="getRealEstateName"
              @select="option => selectedRealEstate = option">
              <template #empty>No se han encontrado resultados</template>
            </b-autocomplete>
          </b-field>
          <b-field label="Nombre de proyecto" horizontal>
            <b-autocomplete
              v-model="project"
              :disabled="readOnly"
              :data="filteredProjects()"
              :clearable="!readOnly"
              size="is-small"
              :keep-first="true"
              data-cy="project-name"
              @typing="getProjectName"
              @select="option => selectedProject = option">
              <template #empty>No se han encontrado resultados</template>
            </b-autocomplete>
          </b-field>
          <b-field :label="$t('deliveryDate')" horizontal expanded>
            <div class="field has-addons">
              <p class="control">
                <a class="button is-static">
                  <b-icon icon="calendar-days" size="is-small" />
                </a>
              </p>
              <b-datepicker
                v-model="deliveryDate"
                :disabled="readOnly"
                locale="es-ES"
                size="is-small"
                placeholder="Selecciona una fecha"
                :date-formatter="d => (d ? formatDate(d) : '')" />
            </div>
          </b-field>
        </div>
      </div>
      <div class="subcard">
        <BInputExpanded
          v-model="localSimulation.offerInputs.commercialValue"
          :disabled="readOnly"
          label="Valor de la propiedad"
          placeholder="ingrese valor de la propiedad"
          prefix="UF"
          size="is-small"
          prefix-position="left"
          data-cy="propertyValue" />
        <BInputExpanded
          v-model="localSimulation.offerInputs.appraisal"
          :disabled="readOnly"
          label="Tasación"
          placeholder="Ingresa tasación"
          prefix="UF"
          size="is-small"
          prefix-position="left"
          data-cy="appraisal" />
      </div>
    </div>
  </b-collapse>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { formatDate, stringToDate } from '@/infrastructure/format';
import BInputExpanded from '@/ui/components/input/BInputExpanded.vue';

export default {
  name: 'PropertyCard',
  components: { BInputExpanded },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showPropertyValues: false,
      localSimulation: {
        generalParams: {
          hasRealEstate: false,
          hasAddress: false
        },
        offerInputs: {
          realEstate: {}
        },
        offerResponse: {
          propertyValue: null
        }
      },
      realEstateNames: [],
      realEstate: '',
      selectedRealEstate: null,
      projectNames: [],
      project: '',
      selectedProject: null
    };
  },
  computed: {
    ...mapState('evaluation', ['simulation']),
    ...mapGetters('evaluation', ['getSimulationCard']),
    commercialValue: {
      get() {
        return this.$getSafe(this.localSimulation, 'offerInputs.commercialValue');
      },
      set(value) {
        this.$set(this.localSimulation.offerInputs, 'commercialValue', value);
      }
    },
    deliveryDate: {
      get() {
        const date = this.$getSafe(this.localSimulation, 'offerInputs.realEstate.project.deliveryDate');
        return stringToDate(date);
      },
      set(value) {
        this.$set(this.localSimulation.offerInputs.realEstate.project, 'deliveryDate', formatDate(stringToDate(value), 'YYYY-MM-DD'));
      }
    },
    projectName: {
      get() {
        return this.$getSafe(this.localSimulation, 'offerInputs.realEstate.project.name');
      },
      set(value) {
        this.$set(this.localSimulation.offerInputs.realEstate.project, 'name', value);
      }
    },
    realEstateName: {
      get() {
        return this.$getSafe(this.localSimulation, 'offerInputs.realEstate.name');
      },
      set(value) {
        this.$set(this.localSimulation.offerInputs.realEstate, 'name', value);
      }
    },
    mainLineAddress: {
      get() {
        return this.$getSafe(this.localSimulation, 'offerInputs.property.address.mainLine');
      },
      set(value) {
        this.$set(this.localSimulation.offerInputs.property.address, 'mainLine', value);
      }
    },
    secondaryLineAddress: {
      get() {
        return this.$getSafe(this.localSimulation, 'offerInputs.property.address.secondaryLine');
      },
      set(value) {
        this.$set(this.localSimulation.offerInputs.property.address, 'secondaryLine', value);
      }
    },
    cityAddress: {
      get() {
        return this.$getSafe(this.localSimulation, 'offerInputs.property.address.city');
      },
      set(value) {
        this.$set(this.localSimulation.offerInputs.property.address, 'city', value);
      }
    },
    countryAddress: {
      get() {
        return this.$getSafe(this.localSimulation, 'offerInputs.property.address.country');
      },
      set(value) {
        this.$set(this.localSimulation.offerInputs.property.address, 'country', value);
      }
    }
  },
  watch: {
    localSimulation: {
      deep: true,
      handler() {
        this.$emit('evaluation/setSimulation', this.localSimulation);
      }
    }
  },
  mounted() {
    this.selectedRealEstate = this.realEstateName;
    this.realEstate = this.selectedRealEstate;
    this.selectedProject = this.projectName;
    this.project = this.selectedProject;
    this.localSimulation = this.simulation;
  },
  methods: {
    formatDate,
    stringToDate,
    togglePropertyValues() {
      this.showPropertyValues = !this.showPropertyValues;
    },
    getRealEstateName() {
      this.realEstateNames = [this.realEstateName];
      this.selectedRealEstate = this.realEstateName;
      this.realEstate = this.selectedRealEstate;
    },
    getProjectName() {
      this.projectNames = [this.projectName];
      this.selectedProject = this.projectName;
      this.project = this.selectedProject;
    },
    filteredRealEstates() {
      this.selectedRealEstate = this.realEstateName;
      this.realEstate = this.selectedRealEstate;
      return this.realEstateNames.filter(option => {
        return option
          .toString()
          .toLowerCase()
          .indexOf(this.realEstate.toLowerCase()) >= 0;
      });
    },
    filteredProjects() {
      this.selectedProject = this.projectName;
      this.project = this.selectedProject;
      return this.projectNames.filter(option => {
        return option
          .toString()
          .toLowerCase()
          .indexOf(this.project.toLowerCase()) >= 0;
      });
    }
  }
};
</script>

<style scoped>
</style>
